import { makeStyles } from '@material-ui/core/styles';
import {
  colorMap,
  TextColors,
  BackgroundColors,
  TextOpacities,
  SecondaryColors,
} from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const darkTheme = {
  root: {
    '& $formHeading': {
      color: TextColors.light,
    },

    '& $form': {
      color: TextColors.light,

      '&:not(.is-outline)': {
        '& .MuiInputBase-root': {
          backgroundColor: BackgroundColors.dark,
        },
      },

      '& .MuiInputBase-root': {
        color: TextColors.light,

        '&:not(.Mui-error)': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: hexToRGBA(TextColors.light, TextOpacities.light3),
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: TextColors.light,
          },
        },
      },

      '& .MuiFormLabel-root': {
        '&:not(.Mui-error)': {
          color: hexToRGBA(TextColors.light, TextOpacities.light3),

          '&.Mui-focused, &.MuiFormLabel-filled': {
            color: TextColors.light,
          },
        },
      },
    },

    '& $selectIcon': {
      color: SecondaryColors.main,
    },

    '& $preloader': {
      color: TextColors.light,
    },

    '& $radioOrCheckbox': {
      '&:hover, &:focus-within': {
        // backgroundColor: hexToRGBA(TextColors.dark, 0.1),

        '&.Mui-checked': {
          // backgroundColor: hexToRGBA(SecondaryColors.main, 0.1),
        },
      },
      '&.MuiRadio-root:not(.Mui-checked)': {
        color: hexToRGBA(TextColors.light, TextOpacities.light3),
      },
    },

    '& $datePicker': {
      '& .MuiInputBase-input': {
        color: TextColors.light,
      },

      '&:not(.error) .MuiInput-underline::before': {
        borderColor: hexToRGBA(TextColors.light, TextOpacities.light3),
      },

      '&:not(.error) .MuiIconButton-label': {
        color: hexToRGBA(TextColors.light, TextOpacities.light3),
      },
    },
  },
};

const lightTheme = {
  root: {
    '& $formHeading': {
      color: TextColors.dark,
    },

    '& $form': {
      color: TextColors.dark,

      '&:not(.is-outline)': {
        '& .MuiInputBase-root': {
          backgroundColor: BackgroundColors.light,
        },
      },

      '& .MuiInputBase-root': {
        color: TextColors.dark,

        '&:not(.Mui-error)': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: hexToRGBA(TextColors.dark, TextOpacities.dark3),
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: TextColors.dark,
          },
        },
      },

      '& .MuiFormLabel-root': {
        '&:not(.Mui-error)': {
          color: hexToRGBA(TextColors.dark, TextOpacities.dark3),

          '&.Mui-focused, &.MuiFormLabel-filled': {
            color: TextColors.dark,
          },
        },
      },
    },

    '& $selectIcon': {
      color: SecondaryColors.dark,
    },

    '& $preloader': {
      color: TextColors.dark,
    },

    '& $radioOrCheckbox': {
      '&:hover, &:focus-within': {
        // backgroundColor: hexToRGBA(TextColors.light, 0.1),

        '&.Mui-checked': {
          // backgroundColor: hexToRGBA(SecondaryColors.main, 0.1),
        },
      },
      '&.MuiRadio-root:not(.Mui-checked)': {
        // color: hexToRGBA(TextColors.dark, TextOpacities.dark3),
      },
    },

    '& $datePicker': {
      '& .MuiInputBase-input': {
        color: TextColors.dark,
      },

      '&:not(.error) .MuiInput-underline::before': {
        borderColor: hexToRGBA(TextColors.dark, TextOpacities.dark3),
      },

      '&:not(.error) .MuiIconButton-label': {
        color: hexToRGBA(TextColors.dark, TextOpacities.dark3),
      },
    },
  },
};

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.dark-theme': darkTheme.root,
      '&.light-theme': lightTheme.root,
      '& .Mui-error-text, .Mui-error-text > *': {
        color: '#f44336 !important',
        '&.small': {
          fontSize: '12px',
          fontStyle: 'normal',
          fontFamily: 'Circular, helvetica, arial, sans-serif',
          fontWeight: 'normal',
          lineHeight: 1.6,
          fontVariant: 'normal',
          letterSpacing: 'normal',
        },
      },
    },
    marketoForm: {
      height: '0 !important',
      width: '0 !important',
      overflow: 'hidden',
    },
    formHeading: {
      '&.MuiTypography-root': {
        marginBottom: muiTheme.spacing(2),
      },
    },
    formDescription: {
      marginBottom: muiTheme.spacing(4),
    },
    form: {
      '&.bg-color-white .MuiFormLabel-root': {
        '&.Mui-focused, &.MuiFormLabel-filled': {
          backgroundColor: colorMap.white,
          borderColor: colorMap.white,
        },
      },

      '&.bg-color-eggshell .MuiFormLabel-root': {
        '&.Mui-focused, &.MuiFormLabel-filled': {
          backgroundColor: colorMap.eggshell,
          borderColor: colorMap.eggshell,
        },
      },

      '&.bg-color-charcoal .MuiFormLabel-root': {
        '&.Mui-focused, &.MuiFormLabel-filled': {
          backgroundColor: colorMap.charcoal,
          borderColor: colorMap.charcoal,
        },
      },

      '&.bg-color-medium-gray .MuiFormLabel-root': {
        '&.Mui-focused, &.MuiFormLabel-filled': {
          backgroundColor: colorMap['medium-gray'],
          borderColor: colorMap['medium-gray'],
        },
      },

      '&.bg-color-dark-gray .MuiFormLabel-root': {
        '&.Mui-focused, &.MuiFormLabel-filled': {
          backgroundColor: colorMap['dark-gray'],
          borderColor: colorMap['dark-gray'],
        },
      },

      '&.bg-color-black .MuiFormLabel-root': {
        '&.Mui-focused, &.MuiFormLabel-filled': {
          backgroundColor: colorMap.black,
          borderColor: colorMap.black,
        },
      },

      '&:not(.is-outline)': {
        '& .MuiInputBase-root': {
          '&:not(.Mui-error)': {
            '& .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
              display: 'none',
            },
          },
        },
      },

      '& .MuiFormControlLabel-root': {
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        marginLeft: -9,
      },

      '& .MuiFormLabel-root': {
        lineHeight: 1,

        '&.Mui-focused, &.MuiFormLabel-filled': {
          borderWidth: 2,
          borderStyle: 'solid',
          borderRadius: '4px',
          left: '-5px',
          padding: '0px 5px 2px 5px',
        },
      },

      '& a.mktoNotYou': {
        cursor: 'pointer',
      },
    },
    formControl: {},
    legend: {
      marginBottom: muiTheme.spacing(1),
    },
    select: {
      '&.Mui-error, & .Mui-error': {
        '& $selectIcon.MuiSvgIcon-root': {
          color: muiTheme.palette.error.main,
        },
      },

      '& .MuiSelect-selectMenu': {
        lineHeight: 'normal',
        minHeight: '1.1876em',
      },
    },
    selectIcon: {
      '&.MuiSelect-icon': {
        right: '10px',
        top: 'calc(50% - 10px)',
      },
    },
    modalContainer: {
      '&.MuiContainer-root': {
        padding: 0,
        width: '100vw',

        [muiTheme.breakpoints.up('sm')]: {
          width: 'calc(100vw - 64px)',
        },

        [muiTheme.breakpoints.up('md')]: {
          width: muiTheme.breakpoints.width('md'),
        },
      },
    },
    preloaderContainer: {
      position: 'relative',
      minHeight: '64px',
      minWidth: '64px',

      '&.has-text': {
        marginTop: muiTheme.spacing(2),
      },
    },
    preloader: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      alignItems: 'center',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
    },
    modalBackground: {
      backgroundColor: colorMap.white,
      padding: muiTheme.spacing(10),
      minHeight: '224px',
      minWidth: '224px',

      [muiTheme.breakpoints.down('sm')]: {
        minHeight: '100vh',
        width: '100%',
        padding: muiTheme.spacing(4),
        paddingTop: muiTheme.spacing(10),
      },
    },
    modalClose: {
      '&.MuiButtonBase-root': {
        position: 'absolute',
        top: muiTheme.spacing(1),
        right: muiTheme.spacing(1),
      },
    },
    textField: {},
    radioOrCheckbox: {
      '&.MuiButtonBase-root': {
        marginTop: '-4px',
        marginBottom: '-4px',
      },
      '&.MuiRadio-root': {
        marginTop: '-8px',
      },
    },
    submit: {},
    recaptchaLegal: {
      'margin-top': muiTheme.spacing(2),
    },
    datePicker: {
      '&.error .MuiInput-underline::before': {
        borderColor: muiTheme.palette.error.main,
      },

      '&.error .MuiIconButton-label': {
        color: muiTheme.palette.error.main,
      },
    },
  }),
  { name: 'GenMarketoForm' },
);

export default useStyles;
